import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil, Trash2, Loader2 } from "lucide-react";

const FIELDS = [
  ["name", "Full name", "sm:col-span-2"], ["phone", "Phone"], ["email", "Email"],
  ["property_address", "Property address", "sm:col-span-2"], ["city", "City"], ["state", "State"], ["zip_code", "ZIP"],
];
const TYPES = ["Purchase", "Refinance", "HELOC", "Reverse Mortgage", "Cash-Out Refinance"];

export const EditLeadButton = ({ p, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const start = () => { setF(Object.fromEntries([...FIELDS.map(([k]) => [k, p[k] || ""]), ["opportunity_type", p.opportunity_type || ""]])); setOpen(true); };
  const save = async () => {
    setBusy(true);
    try { await api.put(`/prospects/${p.id}/details`, f); toast.success("Lead updated"); setOpen(false); onSaved?.(); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={start} data-testid="drawer-edit-lead-btn"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="edit-lead-dialog">
          <DialogHeader><DialogTitle>Edit lead</DialogTitle><DialogDescription>Mailing address is edited from the mail badge.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {FIELDS.map(([k, label, span]) => (
              <div key={k} className={span || ""}><Label className="text-xs">{label}</Label>
                <Input className="mt-1" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`edit-lead-${k}`} /></div>
            ))}
            <div className="sm:col-span-2"><Label className="text-xs">Loan type</Label>
              <Select value={f.opportunity_type || ""} onValueChange={(v) => setF({ ...f, opportunity_type: v })}>
                <SelectTrigger className="mt-1" data-testid="edit-lead-type"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent>{[...new Set([...TYPES, f.opportunity_type].filter(Boolean))].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter><Button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="edit-lead-save">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const DeleteLeadButton = ({ p, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const del = async () => {
    try { await api.delete(`/prospects/${p.id}`); toast.success("Lead deleted"); onDeleted?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" onClick={() => setOpen(true)} data-testid="drawer-delete-lead-btn"><Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete</Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="delete-lead-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {p.name || "this lead"}?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the lead and its call and email history. The homeowner becomes available to other MLOs again.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-lead-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700" data-testid="delete-lead-confirm">Delete lead</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
